import { create } from 'zustand';

type Status = 'success' | 'error' | 'warning' | 'info';
type Variant = 'simple' | 'filled' | 'outlined';

export interface AlertAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md' | 'lg';
}

export interface AlertProps {
  id: string;
  text: string;
  message?: string;
  status: Status;
  variant?: Variant;
  actions?: AlertAction[];
  autoClose?: number | false;
  close?: boolean;
}

interface AlertStore {
  alerts: AlertProps[];
  showAlert: (alert: Omit<AlertProps, 'id'>) => void;
  removeAlert: (id: string) => void;
}

export const useAlertStore = create<AlertStore>((set) => ({
  alerts: [],
  showAlert: (alert) => {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    const id = array[0].toString(36);
    const alertWithDefaults: AlertProps = {
      variant: 'simple',
      close: true,
      ...alert,
      id,
    };

    set((state) => ({
      alerts: [...state.alerts, alertWithDefaults]
    }));

    // Auto-remove if autoClose is set
    if (alert.autoClose !== false) {
      setTimeout(() => {
        set((state) => ({
          alerts: state.alerts.filter((a) => a.id !== id)
        }));
      }, alert.autoClose || 5000);
    }
  },
  removeAlert: (id) => {
    set((state) => ({
      alerts: state.alerts.filter((alert) => alert.id !== id)
    }));
  },
}));

// Export a convenience function to show alerts from anywhere
export const showAlert = (
  text: string,
  options: {
    message?: string;
    status?: Status;
    variant?: Variant;
    actions?: AlertAction[];
    autoClose?: number | false;
    close?: boolean;
  } = {}
) => {
  const { showAlert: show } = useAlertStore.getState();
  show({
    text,
    message: options?.message,
    status: options?.status || 'info',
    variant: options?.variant || 'simple',
    actions: options?.actions,
    autoClose: options?.autoClose ?? 5000,
    close: options?.close !== false,
  });
};

// Re-exportar los tipos para facilitar su uso
export type { Status, Variant };
